/* eslint-disable no-underscore-dangle */
import * as yup from "yup";
import { formatISO, isValid, parseISO } from "date-fns";
import { makeFormatDate } from "utils/formatting";
import { ComparisonRef, getRefLabel, isRef } from "utils/schemas/utils";

type EmptyObj = Record<string, never>;

type ComparisonExtraParams = {
  comparisonRef?: ComparisonRef;
  comparisonValue: ISODate;
  getComparisonLabel: () => string | undefined;
  getFormattedComparisonValue: (value: ISODate) => string;
};

type ComparisonTestOptions = {
  message?: yup.Message<ComparisonExtraParams>;
  isExclusive?: boolean;
};

function makeGetComparisonMessage(
  comparisonDescription: string,
): yup.Message<ComparisonExtraParams> {
  return ({
    label,
    comparisonValue,
    getComparisonLabel,
    getFormattedComparisonValue,
  }) => {
    const formattedComparisonValue =
      getFormattedComparisonValue(comparisonValue);
    const comparisonLabel = getComparisonLabel();
    return `${label ? `${label} m` : "M"}ust be ${comparisonDescription} ${
      comparisonLabel
        ? `${comparisonLabel} (${formattedComparisonValue})`
        : formattedComparisonValue
    }.`;
  };
}

function ensureDate(date: ISODate | Date) {
  if (typeof date === "string") return parseISO(date);
  return date;
}

type Options = {
  dateFormat?: string;
  dateFormatter?: (date: Date | ISODate) => string;
  isDateTime?: boolean;
};

class ISODateStringSchema<
  TType extends yup.Maybe<ISODate> = ISODate | undefined,
  TContext = yup.AnyObject,
  TDefault = undefined,
  TFlags extends yup.Flags = "",
> extends yup.Schema<TType, TContext, TDefault, TFlags> {
  private options: Options | undefined;

  private _nestedFromCache: Partial<{ [key: string]: yup.ISchema<unknown> }> =
    {};

  constructor(options?: Options) {
    super({
      type: "iso-date-string",
      check: (value: unknown): value is NonNullable<TType> => {
        if (typeof value !== "string") return false;
        if (!value) return false;
        return isValid(parseISO(value));
      },
    });

    this.withMutation(() => {
      this.transform(
        (value: ISODate | Date | null | undefined, originalValue, context) => {
          if (!context.spec.coerce) return value;
          if (value instanceof Date || typeof value === "number") {
            return formatISO(value, {
              representation: options?.isDateTime ? "complete" : "date",
            });
          }
          return value;
        },
      );
    });

    this.options = options;
  }

  private prepareParam(
    ref: unknown,
    name: string,
  ): Date | ISODate | yup.Reference<Date | ISODate> {
    if (isRef(ref)) {
      return ref as yup.Reference<Date | ISODate>;
    }

    const cast = this.cast(ref);
    if (!this._typeCheck(cast))
      throw new TypeError(
        `\`${name}\` must be a Date or a value that can be \`cast()\` to a Date`,
      );
    return cast;
  }

  private formatDate(date: Date | ISODate) {
    if (this.options?.dateFormatter) return this.options.dateFormatter(date);
    return makeFormatDate(
      this.options?.dateFormat || (this.options?.isDateTime ? "Pp" : "P"),
    )(date);
  }

  private useFromSchemaCache(key: string) {
    return {
      getFromSchema: () => this._nestedFromCache?.[key],
      setWithContext: (context: yup.TestContext<TContext>) => {
        if (!this._nestedFromCache) this._nestedFromCache = {};
        this._nestedFromCache[key] = context.from?.[0]?.schema;
      },
    };
  }

  private makeComparisonTest(
    name: string,
    test: (value: Date, comparisonValue: Date) => boolean,
  ) {
    return (
      comparisonValueOrRef: Date | ISODate | yup.Reference<Date | ISODate>,
      message: yup.Message<ComparisonExtraParams>,
    ) => {
      const limit = this.prepareParam(comparisonValueOrRef, name);

      const { getFromSchema, setWithContext } = this.useFromSchemaCache(name);

      const comparisonRef = isRef(limit)
        ? { ref: limit, getFromSchema }
        : undefined;

      return this.test({
        message,
        name,
        exclusive: true,
        params: {
          comparisonValue: limit,
          comparisonRef,
          getComparisonLabel: () =>
            comparisonRef ? getRefLabel(comparisonRef) : undefined,
          getFormattedComparisonValue: (v) => this.formatDate(v),
        } as ComparisonExtraParams,
        skipAbsent: true,
        test(value, context) {
          setWithContext(context);
          const comparisonValue = this.resolve(limit);
          if (!comparisonValue) return true;
          const comparisonDate = ensureDate(comparisonValue);
          if (!isValid(comparisonDate)) return true;
          return test(ensureDate(value!), comparisonDate);
        },
      });
    };
  }

  min(
    min: Date | ISODate | yup.Reference<Date | ISODate>,
    { isExclusive, message }: ComparisonTestOptions = {},
  ) {
    return this.makeComparisonTest("min", (value, comparisonValue) =>
      isExclusive ? value > comparisonValue : value >= comparisonValue,
    )(
      min,
      message ||
        makeGetComparisonMessage(`${isExclusive ? "" : "on or "}after`),
    );
  }

  max(
    max: Date | ISODate | yup.Reference<Date | ISODate>,
    { isExclusive, message }: ComparisonTestOptions = {},
  ) {
    return this.makeComparisonTest("max", (value, comparisonValue) =>
      isExclusive ? value < comparisonValue : value <= comparisonValue,
    )(
      max,
      message ||
        makeGetComparisonMessage(`${isExclusive ? "" : "on or "}before`),
    );
  }

  default<D extends yup.Maybe<TType>>(def: yup.DefaultThunk<D, TContext>) {
    return super.default(def) as ISODateStringSchema<
      TType,
      TContext,
      TDefault,
      TFlags
    >;
  }

  defined(message?: yup.Message<EmptyObj> | undefined) {
    return super.defined(message) as ISODateStringSchema<
      yup.Defined<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }

  required(message?: yup.Message<EmptyObj> | undefined) {
    return super.required(message) as ISODateStringSchema<
      NonNullable<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }

  notRequired() {
    return super.notRequired() as ISODateStringSchema<
      yup.Maybe<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }

  nullable() {
    return super.nullable() as ISODateStringSchema<
      TType | null,
      TContext,
      TDefault,
      TFlags
    >;
  }

  nonNullable(message?: yup.Message<EmptyObj> | undefined) {
    return super.nonNullable(message) as ISODateStringSchema<
      yup.NotNull<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }
}

export default function isoDateStr(options?: Options) {
  return new ISODateStringSchema(options);
}

export function definedIsoDateStr(options?: Options) {
  return isoDateStr(options).nullable().defined().default(null);
}
